/**
 * Portal Token Service
 *
 * Singleton that manages portal access token lifecycle:
 * - Proactive refresh before expiry (5-minute window)
 * - 401-triggered refresh with cross-tab coordination via TokenManager
 * - Session persistence to localStorage for page reload / cross-tab sync
 *
 * Uses the same TokenManager from libs/core that the CRM uses, configured
 * with a 'portal_auth' namespace so keys don't collide.
 */

import { TokenManager, setWorkdItem } from '@supply-mover-2-0/core';
import { useLoginModalStore } from '../stores/loginModalStore';
import {
	PORTAL_AUTH_STORAGE_KEY,
	usePortalAuthStore,
} from '../stores/portalAuthStore';

const AUTH_SERVER_URL = import.meta.env.VITE_AUTH_SERVER_URL as string;

/** 5 minutes in ms — refresh proactively before expiry */
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

interface PortalRefreshResponse {
	accessToken: string;
	refreshToken: string;
	expiresIn: number;
}

/**
 * Parse the access token from a portal auth localStorage event.
 * The portal stores { token, refreshToken, expiresAt } at the storage key.
 */
function parseTokenFromStorageEvent(newValue: string): string | null {
	try {
		const parsed = JSON.parse(newValue);
		return parsed?.token ?? null;
	} catch {
		return null;
	}
}

// --- Singleton TokenManager instance ---

const tokenManager = new TokenManager({
	namespace: 'portal_auth',
	getTabId: () => usePortalAuthStore.getState().tabId,
	setTabId: (id: string) => usePortalAuthStore.getState().setTabId(id),
	parseTokenFromStorageEvent,
});

/**
 * Exchange the current refresh token for a new access token.
 * Uses the dedicated portal refresh endpoint (POST /auth/portal/refresh)
 * which preserves the PortalUser token metadata through token rotation.
 */
async function doRefreshToken(): Promise<string | null> {
	const { refreshToken } = usePortalAuthStore.getState();

	if (refreshToken == null) {
		return null;
	}

	const response = await fetch(`${AUTH_SERVER_URL}/auth/portal/refresh`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ refreshToken }),
	});

	if (!response.ok) {
		// Refresh token is invalid/expired — clear auth state and prompt re-login
		console.debug(
			'[PortalTokenService] Refresh token rejected, prompting re-login',
		);
		usePortalAuthStore.getState().reset();
		useLoginModalStore
			.getState()
			.open('Your session has expired. Please sign in again.');
		return null;
	}

	const data = (await response.json()) as PortalRefreshResponse;

	const store = usePortalAuthStore.getState();
	const newExpiresAt = Date.now() + data.expiresIn * 1000;

	store.setToken(data.accessToken);
	store.setRefreshToken(data.refreshToken);
	store.setExpiresAt(newExpiresAt);
	store.persist();

	// Also write to the workd: namespaced key so the TokenManager's
	// cross-tab storage event listeners in other tabs can pick it up
	setWorkdItem('portal_auth', 'state', {
		token: data.accessToken,
		refreshToken: data.refreshToken,
		expiresAt: newExpiresAt,
	});

	return data.accessToken;
}

/**
 * Check whether the current access token needs refreshing.
 */
function needsRefresh(): boolean {
	const { expiresAt, token } = usePortalAuthStore.getState();
	if (token == null || expiresAt == null) return true;
	return Date.now() >= expiresAt || expiresAt - Date.now() < REFRESH_BUFFER_MS;
}

/**
 * Get a valid access token, refreshing if necessary.
 * Coordinates across tabs so only one refresh happens at a time.
 * Returns null if the user is not authenticated and refresh fails.
 */
export async function getValidToken(): Promise<string | null> {
	const { token, refreshToken, isAuthenticated } =
		usePortalAuthStore.getState();

	if (!isAuthenticated && refreshToken == null) {
		return null;
	}

	if (!needsRefresh() && token != null) {
		return token;
	}

	// Token needs refresh — coordinate across tabs
	try {
		const newToken = await tokenManager.coordinateRefresh(doRefreshToken);
		return newToken;
	} catch (error) {
		console.error('[PortalTokenService] Token refresh failed:', error);
		return null;
	}
}

/**
 * Force a token refresh. Called by apiClient on 401 responses.
 * Returns the new token or null if refresh failed (user will be logged out).
 */
export async function refreshAccessToken(): Promise<string | null> {
	const { refreshToken } = usePortalAuthStore.getState();

	if (refreshToken == null) {
		return null;
	}

	try {
		const newToken = await tokenManager.coordinateRefresh(doRefreshToken);
		return newToken;
	} catch (error) {
		console.error('[PortalTokenService] Token refresh failed:', error);
		return null;
	}
}

/**
 * Listen for auth state changes from other tabs.
 * When another tab refreshes the token, update this tab's store.
 */
export function setupCrossTabSync(): () => void {
	// Cross-tab sync is implicitly brand-scoped via browser origin isolation:
	// sibling brands live on different subdomains, and storage events don't
	// cross origins. So a single storage-key match is sufficient here.
	const handler = (e: StorageEvent): void => {
		if (e.key !== PORTAL_AUTH_STORAGE_KEY || e.newValue == null) return;

		try {
			const parsed = JSON.parse(e.newValue);
			if (parsed?.token != null) {
				const store = usePortalAuthStore.getState();
				store.setToken(parsed.token);
				store.setRefreshToken(parsed.refreshToken);
				store.setExpiresAt(parsed.expiresAt);
			}
		} catch {
			// Ignore malformed storage events
		}
	};

	// Also handle logout from another tab.
	const logoutHandler = (e: StorageEvent): void => {
		if (e.key === PORTAL_AUTH_STORAGE_KEY && e.newValue == null) {
			usePortalAuthStore.getState().reset();
		}
	};

	window.addEventListener('storage', handler);
	window.addEventListener('storage', logoutHandler);

	return () => {
		window.removeEventListener('storage', handler);
		window.removeEventListener('storage', logoutHandler);
	};
}

/**
 * Cleanup the TokenManager's storage listeners.
 */
export function cleanup(): void {
	tokenManager.cleanup();
}
